import * as React from "react"
import * as SheetPrimitive from "@radix-ui/react-dialog"
import { cva, type VariantProps } from "class-variance-authority"
import { X } from "lucide-react"

import { cn } from '@/lib/core/utils'
import {
  dialogCloseClass,
  dialogDescriptionClass,
  dialogNoiseClass,
  dialogTitleClass,
} from "@/components/ui/dialog-surface"
import { useBottomNavSafe } from '@/context/BottomNavContext'
import { useSwipeToClose } from '@/hooks/ui/useSwipeToClose'

// Context to pass onOpenChange from Sheet to SheetContent
interface SheetContextValue {
  onOpenChange?: (open: boolean) => void;
}

const SheetContext = React.createContext<SheetContextValue>({});

const Sheet = SheetPrimitive.Root

// Wrapper component that provides onOpenChange context
const SheetWithProvider = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Root>
>(({ onOpenChange, ...props }, ref) => (
  <SheetContext.Provider value={{ onOpenChange }}>
    <SheetPrimitive.Root ref={ref} onOpenChange={onOpenChange} {...props} />
  </SheetContext.Provider>
))
SheetWithProvider.displayName = SheetPrimitive.Root.displayName

const SheetTrigger = SheetPrimitive.Trigger

const SheetClose = SheetPrimitive.Close

const SheetPortal = SheetPrimitive.Portal

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-40 bg-black/30 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=open]:duration-300 data-[state=closed]:duration-300",
      className
    )}
    {...props}
  />
))
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName

const sheetVariants = cva(
  "fixed z-40 gap-4 shadow-lg bg-card", // z-40 to stay below navbar (z-50)
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b", // Removed animation classes
        bottom:
          "inset-x-0 bottom-0 border-t data-[state=open]:animate-slide-up data-[state=closed]:animate-slide-down", // Full height bottom sheet with slide animation
        left: "inset-y-0 left-0 h-full w-3/4 border-r", // Removed animation classes
        right:
          "inset-y-0 right-0 h-full w-3/4  border-l", // Removed animation classes
      },
    },
    defaultVariants: {
      side: "right",
    },
  }
)

// Height variants for bottom sheets (used by responsive modal)
const sheetHeightVariants = cva("", {
  variants: {
    height: {
      auto: "max-h-[50vh]", // Simple confirmations, short content
      half: "h-[75vh]", // Medium forms (3-6 fields)
      full: "h-[100dvh]", // Complex multi-tab content
    },
  },
  defaultVariants: {
    height: "auto",
  },
})

// Drag handle component for bottom sheets
const SheetDragHandle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "mx-auto mt-3 mb-2 h-1.5 w-16 shrink-0 rounded-full bg-foreground",
      className
    )}
    {...props}
  />
))
SheetDragHandle.displayName = "SheetDragHandle"

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
  VariantProps<typeof sheetVariants> {
  /** Hide the default close button (for responsive modal which handles its own close) */
  hideDefaultClose?: boolean
  /** Keep the bottom navigation visible when this sheet is open */
  keepBottomNavVisible?: boolean
  /** Enable swipe-down-to-close gesture (default: true for bottom sheets) */
  enableSwipeToClose?: boolean
}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(({ side = "right", className, children, style, hideDefaultClose = false, enableSwipeToClose, ...props }, ref) => {
  const contentRef = React.useRef<HTMLElement>(null);
  const bottomNav = useBottomNavSafe();
  const { onOpenChange } = React.useContext(SheetContext);

  // Merge the forwarded ref with our local ref
  React.useImperativeHandle(ref, () => contentRef.current!);

  // Check if this is a bottom sheet with custom origin animation
  const hasCustomOrigin = side === "bottom" && style && ('--menu-button-top' in style || '--menu-button-right' in style);

  // Determine if swipe-to-close should be enabled
  const shouldEnableSwipe = enableSwipeToClose ?? (side === "bottom");

  // Swipe-to-close hook for bottom sheets
  const { swipeOffset, isDragging } = useSwipeToClose({
    onClose: () => onOpenChange?.(false),
    enabled: shouldEnableSwipe,
    threshold: 100,
    hapticFeedback: true,
    contentRef,
  });

  // Hide mobile bottom nav when bottom sheet is open via context
  React.useEffect(() => {
    if (side !== "bottom" || !bottomNav) return;

    const element = contentRef.current;
    if (!element) return;

    // Handle state changes for the bottom nav visibility
    const handleStateChange = () => {
      const state = element.getAttribute('data-state');
      if (state === 'open') {
        bottomNav.setVisible(false);
      } else if (state === 'closed') {
        bottomNav.setVisible(true);
      }
    };

    // Check initial state
    handleStateChange();

    // Observe data-state changes
    const observer = new MutationObserver(handleStateChange);
    observer.observe(element, {
      attributes: true,
      attributeFilter: ['data-state']
    });

    return () => observer.disconnect();
  }, [side, bottomNav]);

  // Calculate transform style for swipe feedback
  const getTransformStyle = (): React.CSSProperties => {
    if (side !== "bottom" || !shouldEnableSwipe) {
      return hasCustomOrigin
        ? {
            ...style,
            transformOrigin: `calc(100% - var(--menu-button-right, 0px) - 16px) var(--menu-button-top, 0px)`,
          }
        : style;
    }

    return {
      ...style,
      transform: swipeOffset > 0 ? `translateY(${swipeOffset}px)` : undefined,
      transition: isDragging ? 'none' : 'transform 0.3s ease-out',
      ...(hasCustomOrigin && {
        transformOrigin: `calc(100% - var(--menu-button-right, 0px) - 16px) var(--menu-button-top, 0px)`,
      }),
    };
  };

  return (
    <SheetPortal>
      <SheetOverlay className={side === "bottom" ? "top-12" : ""} />
      <SheetPrimitive.Content
        ref={contentRef}
        className={cn(sheetVariants({ side }), dialogNoiseClass(className), className)}
        style={getTransformStyle()}
        {...props}
      >
        <SheetPrimitive.Title className="sr-only">
          Navigation Menu
        </SheetPrimitive.Title>
        <SheetPrimitive.Description className="sr-only">
          Navigation menu for accessing different sections of the application
        </SheetPrimitive.Description>
        {/* Drag handle bar for bottom sheets (iPhone-style) - swipe down to close */}
        {side === "bottom" && !hasCustomOrigin && !hideDefaultClose && (
          <div
            className="flex justify-center py-2 w-full cursor-grab active:cursor-grabbing"
            data-slot="sheet-drag-handle"
            onClick={() => onOpenChange?.(false)}
            role="button"
            tabIndex={0}
            aria-label="Close"
          >
            <div className="h-1.5 w-16 shrink-0 rounded-full bg-foreground" />
          </div>
        )}
        {children}
        {side !== "bottom" && (
          <SheetPrimitive.Close className={dialogCloseClass}>
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Content>
    </SheetPortal>
  );
})
SheetContent.displayName = SheetPrimitive.Content.displayName

const SheetHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
))
SheetHeader.displayName = "SheetHeader"

const SheetFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
))
SheetFooter.displayName = "SheetFooter"

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn(dialogTitleClass, className)}
    {...props}
  />
))
SheetTitle.displayName = SheetPrimitive.Title.displayName

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn(dialogDescriptionClass, className)}
    {...props}
  />
))
SheetDescription.displayName = SheetPrimitive.Description.displayName

// Export the wrapper as Sheet instead of the primitive
export { SheetWithProvider as Sheet, SheetClose,
  SheetContent, SheetDescription, SheetDragHandle, SheetFooter, SheetHeader, SheetOverlay, SheetPortal, SheetTitle, SheetTrigger,
  sheetHeightVariants
}

export type { SheetContentProps }
